import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { motion } from 'framer-motion'
import { AlertCircle, Check, FileText, Files, Loader2, X } from 'lucide-react'
import { useSelector } from 'react-redux'
import {
  trackAiDocumentAnalysisCompleted,
  trackAiDocumentAnalysisFailed,
  trackAiDocumentAnalysisStarted,
  resolveAiDocumentAnalysisFailedOutcome,
  privateProjectManualPrecargaContext,
  type AiDocumentAnalysisContext
} from '../../../utils/analytics/aiDocumentAnalysisAnalytics.ts'
import DocumentInsightPanel from '../../../components/Dashboard/DashboardHeader/ConversationalMode/DocumentInsightPanel/DocumentInsightPanel.tsx'
import DocumentPipelineLoopLoader from '../../../components/Dashboard/DashboardHeader/ConversationalMode/DocumentPipelineLoopLoader/DocumentPipelineLoopLoader.tsx'
import ProjectAutofillCard from '../../../components/Dashboard/DashboardHeader/ConversationalMode/MessageBubble/ProjectAutofillCard.tsx'
import ModalOverlay from '../../../components/UI/ModalOverlay/ModalOverlay.tsx'
import {
  createProjectAutofillDraft,
  extractProjectAutofillDraftIdFromResponse,
  getProjectAutofillDraft,
  postProjectAutofillDraftFile,
  postProjectAutofillDraftFromDocument,
  postProjectAutofillDocumentationAnalyzeBatch,
  waitForProjectAutofillDocumentationPipeline,
  PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES,
  formatDocumentationPipelineFileSize,
  type DocumentInsight
} from '../../../services/projectAutofillDraftService.ts'
import {
  collectUniquePrecargaManualFiles,
  extractDraftMetadataFilesFromResponse,
  isPrecargaManualDocumentationFile,
  PRECARGA_MANUAL_DOCUMENTATION_FILE_ACCEPT,
  projectAutofillDraftApiToCardPayload,
  type DraftMetadataFileEntry,
  type ProjectAutofillPayload
} from '../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import styles from './ManualAssistedProjectPrecarga.module.scss'

const isNoisePrecargaErrorMessage = (msg: string | null | undefined): boolean =>
  typeof msg === 'string' && msg.includes('ManualAssistedProjectPrecarga_error')

const fileListKey = (f: File): string =>
  `${f.name || ''}\u0000${f.size}\u0000${f.lastModified}`

type MultiUploadProgressRow = {
  key: string
  name: string
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
}

type PrecargaProcessingUi =
  | { kind: 'idle' }
  | { kind: 'multi_upload'; rows: MultiUploadProgressRow[] }
  | { kind: 'multi_enqueue' }

const resolveBatchPartialRows = (
  metaFiles: DraftMetadataFileEntry[] | null,
  perFileErrors: Record<string, string> | null | undefined,
  perFileSkipped: Record<string, string> | null | undefined
): { fileLabel: string; message: string; tone: 'error' | 'skipped' }[] => {
  const labelForId = (id: string): string => {
    const entry = metaFiles?.find((x) => x?.id != null && String(x.id) === id)
    return (
      (entry?.originalName && String(entry.originalName).trim()) ||
      (entry?.name && String(entry.name).trim()) ||
      id
    )
  }
  const rows: { fileLabel: string; message: string; tone: 'error' | 'skipped' }[] = []
  if (perFileErrors) {
    for (const [id, message] of Object.entries(perFileErrors)) {
      rows.push({ fileLabel: labelForId(id), message, tone: 'error' })
    }
  }
  if (perFileSkipped) {
    for (const [id, message] of Object.entries(perFileSkipped)) {
      rows.push({ fileLabel: labelForId(id), message, tone: 'skipped' })
    }
  }
  return rows
}

export type ManualPrecargaPhase = 'pick' | 'processing' | 'editor'

type Phase = ManualPrecargaPhase

type PickFlow = 'home' | 'single' | 'multi'

export interface ManualAssistedProjectPrecargaProps {
  onBackToClassic: () => void
  variant?: 'standalone' | 'embedded'
  onPhaseChange?: (phase: ManualPrecargaPhase) => void
  initialFile?: File | null
  onInitialFileConsumed?: () => void
  initialMultiFiles?: File[] | null
  onInitialMultiFilesConsumed?: () => void
  resumeDraftId?: string | null
  onPrecargaDraftIdChange?: (draftId: string | null) => void
  onPrecargaPickPendingChange?: (pending: boolean) => void
}

const ManualAssistedProjectPrecarga: React.FC<ManualAssistedProjectPrecargaProps> = ({
  onBackToClassic,
  variant = 'standalone',
  onPhaseChange,
  initialFile = null,
  onInitialFileConsumed,
  initialMultiFiles = null,
  onInitialMultiFilesConsumed,
  resumeDraftId = null,
  onPrecargaDraftIdChange,
  onPrecargaPickPendingChange
}) => {
  const edifyUserId = useSelector((state: { general?: { userData?: { id?: string | number } } }) =>
    state.general?.userData?.id != null ? String(state.general.userData.id) : ''
  )
  const [phase, setPhase] = useState<Phase>(() =>
    initialFile != null ? 'processing' : resumeDraftId ? 'processing' : 'pick'
  )
  const [pickFlow, setPickFlow] = useState<PickFlow>('home')
  const [multiQueue, setMultiQueue] = useState<File[]>([])
  const [precargaProcessingUi, setPrecargaProcessingUi] = useState<PrecargaProcessingUi>({ kind: 'idle' })
  const [submitting, setSubmitting] = useState(false)
  const [pipelinePhase, setPipelinePhase] = useState<string | null>(null)
  const [pipelinePrimaryName, setPipelinePrimaryName] = useState<string | null>(null)
  const [pipelinePrimarySizeBytes, setPipelinePrimarySizeBytes] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pipelineFailureInsight, setPipelineFailureInsight] = useState<DocumentInsight | null>(null)
  const [cardPayload, setCardPayload] = useState<ProjectAutofillPayload | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [batchPartialRows, setBatchPartialRows] = useState<
    { fileLabel: string; message: string; tone: 'error' | 'skipped' }[]
  >([])
  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const multiFileInputRef = useRef<HTMLInputElement>(null)
  const pipelineFailureContinueDraftIdRef = useRef<string | null>(null)
  const pipelineFailureInsightContinueRef = useRef<DocumentInsight | null>(null)
  const multiRunTotalBytesRef = useRef<number | null>(null)
  const initialMultiSeedAppliedRef = useRef(false)

  useEffect(() => {
    onPhaseChange?.(phase)
  }, [phase, onPhaseChange])

  useEffect(() => {
    const pickPending =
      phase === 'pick' &&
      (multiQueue.length > 0 || pickFlow !== 'home' || pipelineFailureInsight != null)
    onPrecargaPickPendingChange?.(pickPending)
  }, [
    phase,
    multiQueue.length,
    pickFlow,
    pipelineFailureInsight,
    onPrecargaPickPendingChange
  ])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const handleBack = useCallback(() => {
    abortRef.current?.abort()
    onBackToClassic()
  }, [onBackToClassic])

  const runSingleFilePipeline = useCallback(
    async (selected: File) => {
      onPrecargaDraftIdChange?.(null)
      pipelineFailureContinueDraftIdRef.current = null
      pipelineFailureInsightContinueRef.current = null
      setError(null)
      setSubmitting(true)
      setPhase('processing')
      setPickFlow('single')
      setPipelinePhase(null)
      setPipelinePrimaryName(null)
      setPipelinePrimarySizeBytes(null)
      setPipelineFailureInsight(null)
      setCardPayload(null)
      setBatchPartialRows([])
      setPrecargaProcessingUi({ kind: 'idle' })
      multiRunTotalBytesRef.current = null

      const ac = new AbortController()
      abortRef.current = ac

      let activeDraftId: string | null = null
      let analysisCtx: AiDocumentAnalysisContext | null = null

      try {
        const fromDoc = await postProjectAutofillDraftFromDocument(selected)
        activeDraftId =
          fromDoc.mode === 'async'
            ? fromDoc.draftId
            : extractProjectAutofillDraftIdFromResponse(fromDoc.body)

        if (!activeDraftId) {
          if (mountedRef.current) {
            setError(
              'No se recibió el borrador. Probá de nuevo o volvé al inicio y cargá los datos a mano.'
            )
            setPhase('pick')
            setPickFlow('home')
          }
          setSubmitting(false)
          return
        }

        analysisCtx = privateProjectManualPrecargaContext(edifyUserId, {
          draftId: activeDraftId,
          uploadMode: fromDoc.mode,
          precargaPath: 'single_file',
          fileCount: 1,
          fileName:
            typeof selected.name === 'string' && selected.name.trim() ? selected.name.trim() : undefined
        })
        trackAiDocumentAnalysisStarted(analysisCtx)

        const finalPipeline = await waitForProjectAutofillDocumentationPipeline(
          activeDraftId,
          ({ loaderPhase, status }) => {
            if (!mountedRef.current) return
            setPipelinePhase(loaderPhase)
            setPipelinePrimaryName(status.primaryFileOriginalName ?? null)
            setPipelinePrimarySizeBytes(status.primaryFileSizeBytes ?? null)
          },
          { signal: ac.signal }
        )

        const rawDraft = await getProjectAutofillDraft(activeDraftId)
        let payload = projectAutofillDraftApiToCardPayload(rawDraft, activeDraftId)
        const pollInsight = finalPipeline.documentInsight ?? null
        if (pollInsight) {
          payload = { ...payload, documentInsight: pollInsight }
        }
        const metaFiles = extractDraftMetadataFilesFromResponse(rawDraft)
        const partialRows = resolveBatchPartialRows(
          metaFiles,
          finalPipeline.documentationBatchPerFileErrors ?? null,
          finalPipeline.documentationBatchPerFileSkipped ?? null
        )
        setBatchPartialRows(partialRows)
        if (mountedRef.current) {
          pipelineFailureContinueDraftIdRef.current = null
          onPrecargaDraftIdChange?.(activeDraftId)
          setCardPayload(payload)
          setPhase('editor')
        }
        trackAiDocumentAnalysisCompleted(analysisCtx, {
          rubroCount: payload.draft?.subprojects?.length,
          batchPartialIssueCount: partialRows.length
        })
      } catch (e: unknown) {
        const named = e as { name?: string; userMessage?: string; message?: string }
        if (named.name === 'AbortError') {
          if (analysisCtx) {
            trackAiDocumentAnalysisFailed(analysisCtx, {
              outcome: 'cancelled',
              errorCode: 'AbortError'
            })
          }
          if (mountedRef.current) {
            pipelineFailureContinueDraftIdRef.current = null
            pipelineFailureInsightContinueRef.current = null
            setPhase('pick')
            setPickFlow('home')
          }
          setSubmitting(false)
          return
        }
        if (axios.isAxiosError(e)) {
          if (analysisCtx) {
            trackAiDocumentAnalysisFailed(analysisCtx, {
              outcome: 'api_error',
              errorCode: e.response?.status != null ? String(e.response.status) : 'axios_error'
            })
          }
          const data = e.response?.data
          if (data && typeof data === 'object') {
            const o = data as Record<string, unknown>
            const msg = o.message ?? o.error ?? o.mensaje
            if (typeof msg === 'string' && msg.trim()) {
              if (mountedRef.current) {
                pipelineFailureContinueDraftIdRef.current = null
                pipelineFailureInsightContinueRef.current = null
                setPipelineFailureInsight(null)
                setError(isNoisePrecargaErrorMessage(msg.trim()) ? null : msg.trim())
                setPhase('pick')
                setPickFlow('home')
              }
              setSubmitting(false)
              return
            }
          }
        }
        if (analysisCtx) {
          trackAiDocumentAnalysisFailed(analysisCtx, resolveAiDocumentAnalysisFailedOutcome(e))
        }
        const errCode = (e as { code?: string }).code
        const errInsight = (e as { documentInsight?: DocumentInsight | null }).documentInsight ?? null
        const hasPipelineInsight =
          errCode === 'DOCUMENTATION_PIPELINE_FAILED' && errInsight != null
        const msg =
          typeof named.userMessage === 'string' && named.userMessage.trim()
            ? named.userMessage.trim()
            : typeof named.message === 'string' && named.message.trim()
              ? named.message.trim()
              : 'No se pudo procesar el documento. Intentá de nuevo.'
        let displayError: string | null = msg
        if (hasPipelineInsight || isNoisePrecargaErrorMessage(displayError)) {
          displayError = null
        }
        if (mountedRef.current) {
          setPipelineFailureInsight(errCode === 'DOCUMENTATION_PIPELINE_FAILED' ? errInsight : null)
          setError(displayError)
          setPhase('pick')
          setPickFlow('single')
          if (hasPipelineInsight && activeDraftId) {
            pipelineFailureContinueDraftIdRef.current = activeDraftId
            pipelineFailureInsightContinueRef.current = errInsight
          } else {
            pipelineFailureContinueDraftIdRef.current = null
            pipelineFailureInsightContinueRef.current = null
          }
        }
      } finally {
        if (mountedRef.current) {
          setSubmitting(false)
        }
        abortRef.current = null
      }
    },
    [edifyUserId, onPrecargaDraftIdChange]
  )

  const runMultiFilePipeline = useCallback(
    async (files: File[]) => {
      onPrecargaDraftIdChange?.(null)
      pipelineFailureContinueDraftIdRef.current = null
      pipelineFailureInsightContinueRef.current = null
      setError(null)
      setSubmitting(true)
      setPhase('processing')
      setPickFlow('multi')
      setPipelinePhase(null)
      setPipelinePrimaryName(null)
      setPipelinePrimarySizeBytes(null)
      setPipelineFailureInsight(null)
      setCardPayload(null)
      setBatchPartialRows([])
      setPrecargaProcessingUi({
        kind: 'multi_upload',
        rows: files.map((f) => ({
          key: fileListKey(f),
          name: (f.name && f.name.trim()) || 'archivo',
          status: 'pending' as const
        }))
      })

      const totalBytes = files.reduce((acc, f) => acc + (typeof f.size === 'number' ? f.size : 0), 0)
      multiRunTotalBytesRef.current = Number.isFinite(totalBytes) ? totalBytes : null
      const primaryLabel =
        files.length === 1
          ? files[0].name?.trim() || '1 archivo'
          : `${files.length} archivos — ${files[0].name?.trim() || 'documentación'}`

      const ac = new AbortController()
      abortRef.current = ac

      let activeDraftId: string | null = null
      let analysisCtx: AiDocumentAnalysisContext | null = null

      try {
        const created = await createProjectAutofillDraft({})
        activeDraftId = extractProjectAutofillDraftIdFromResponse(created)
        if (!activeDraftId) {
          if (mountedRef.current) {
            setError('No se pudo crear el borrador. Probá de nuevo.')
            setPhase('pick')
            setPickFlow('multi')
            setPrecargaProcessingUi({ kind: 'idle' })
          }
          setSubmitting(false)
          return
        }

        for (let i = 0; i < files.length; i++) {
          if (ac.signal.aborted) {
            const err = new Error('Aborted') as Error & { name?: string }
            err.name = 'AbortError'
            throw err
          }
          const f = files[i]
          setPrecargaProcessingUi((prev) => {
            if (prev.kind !== 'multi_upload') return prev
            const rows = prev.rows.map((row, j) =>
              j === i ? { ...row, status: 'uploading' as const } : row
            )
            return { kind: 'multi_upload', rows }
          })
          try {
            await postProjectAutofillDraftFile(activeDraftId, f)
            setPrecargaProcessingUi((prev) => {
              if (prev.kind !== 'multi_upload') return prev
              const rows = prev.rows.map((row, j) =>
                j === i ? { ...row, status: 'done' as const } : row
              )
              return { kind: 'multi_upload', rows }
            })
          } catch (upErr) {
            const msg =
              axios.isAxiosError(upErr) && upErr.response?.data && typeof upErr.response.data === 'object'
                ? String(
                    (upErr.response.data as Record<string, unknown>).message ??
                      (upErr.response.data as Record<string, unknown>).error ??
                      ''
                  ).trim() || 'Error al subir el archivo.'
                : 'Error al subir el archivo.'
            setPrecargaProcessingUi((prev) => {
              if (prev.kind !== 'multi_upload') return prev
              const rows = prev.rows.map((row, j) =>
                j === i ? { ...row, status: 'error' as const, error: msg } : row
              )
              return { kind: 'multi_upload', rows }
            })
            throw upErr
          }
        }

        const rawAfterUploads = await getProjectAutofillDraft(activeDraftId)
        const metaFiles = extractDraftMetadataFilesFromResponse(rawAfterUploads)
        const fileIds = (metaFiles || [])
          .map((x) => ({
            id: x?.id != null ? String(x.id).trim() : '',
            url: x?.storageUrl != null ? String(x.storageUrl).trim() : ''
          }))
          .filter((x) => x.id && x.url)
          .map((x) => x.id)

        if (fileIds.length === 0) {
          if (mountedRef.current) {
            setError('No se pudieron registrar los archivos en el borrador. Revisá la conexión e intentá de nuevo.')
            setPhase('pick')
            setPickFlow('multi')
            setPrecargaProcessingUi({ kind: 'idle' })
          }
          setSubmitting(false)
          return
        }

        if (mountedRef.current) {
          setPrecargaProcessingUi({ kind: 'multi_enqueue' })
        }

        const batchRes = await postProjectAutofillDocumentationAnalyzeBatch(activeDraftId, fileIds)
        if (batchRes.enqueued === false) {
          const skip =
            typeof batchRes.skippedReason === 'string' && batchRes.skippedReason.trim()
              ? batchRes.skippedReason.trim()
              : 'No se pudo iniciar el análisis.'
          if (mountedRef.current) {
            setError(skip)
            setPhase('pick')
            setPickFlow('multi')
            setPrecargaProcessingUi({ kind: 'idle' })
          }
          setSubmitting(false)
          return
        }

        if (mountedRef.current) {
          setPrecargaProcessingUi({ kind: 'idle' })
        }

        analysisCtx = privateProjectManualPrecargaContext(edifyUserId, {
          draftId: activeDraftId,
          uploadMode: 'batch_files',
          precargaPath: 'multi_file_same_obra',
          fileCount: fileIds.length
        })
        trackAiDocumentAnalysisStarted(analysisCtx)

        if (mountedRef.current) {
          setPipelinePrimaryName(primaryLabel)
          setPipelinePrimarySizeBytes(multiRunTotalBytesRef.current)
        }

        const finalPipeline = await waitForProjectAutofillDocumentationPipeline(
          activeDraftId,
          ({ loaderPhase, status }) => {
            if (!mountedRef.current) return
            setPipelinePhase(loaderPhase)
            if (status.primaryFileOriginalName) {
              setPipelinePrimaryName(status.primaryFileOriginalName)
            }
            if (status.primaryFileSizeBytes != null && status.primaryFileSizeBytes > 0) {
              setPipelinePrimarySizeBytes(status.primaryFileSizeBytes)
            }
          },
          { signal: ac.signal }
        )

        const rawDraft = await getProjectAutofillDraft(activeDraftId)
        let payload = projectAutofillDraftApiToCardPayload(rawDraft, activeDraftId)
        const pollInsight = finalPipeline.documentInsight ?? null
        if (pollInsight) {
          payload = { ...payload, documentInsight: pollInsight }
        }
        const metaAfter = extractDraftMetadataFilesFromResponse(rawDraft)
        const partialRows = resolveBatchPartialRows(
          metaAfter,
          finalPipeline.documentationBatchPerFileErrors ?? null,
          finalPipeline.documentationBatchPerFileSkipped ?? null
        )
        setBatchPartialRows(partialRows)
        if (mountedRef.current) {
          pipelineFailureContinueDraftIdRef.current = null
          onPrecargaDraftIdChange?.(activeDraftId)
          setCardPayload(payload)
          setMultiQueue([])
          setPhase('editor')
        }
        trackAiDocumentAnalysisCompleted(analysisCtx, {
          rubroCount: payload.draft?.subprojects?.length,
          batchPartialIssueCount: partialRows.length
        })
      } catch (e: unknown) {
        const named = e as { name?: string; userMessage?: string; message?: string }
        if (named.name === 'AbortError') {
          if (analysisCtx) {
            trackAiDocumentAnalysisFailed(analysisCtx, {
              outcome: 'cancelled',
              errorCode: 'AbortError'
            })
          }
          if (mountedRef.current) {
            pipelineFailureContinueDraftIdRef.current = null
            pipelineFailureInsightContinueRef.current = null
            setPhase('pick')
            setPickFlow('multi')
            setPrecargaProcessingUi({ kind: 'idle' })
          }
          setSubmitting(false)
          return
        }
        if (axios.isAxiosError(e)) {
          if (analysisCtx) {
            trackAiDocumentAnalysisFailed(analysisCtx, {
              outcome: 'api_error',
              errorCode: e.response?.status != null ? String(e.response.status) : 'axios_error'
            })
          }
          const data = e.response?.data
          if (data && typeof data === 'object') {
            const o = data as Record<string, unknown>
            const msg = o.message ?? o.error ?? o.mensaje
            if (typeof msg === 'string' && msg.trim()) {
              if (mountedRef.current) {
                pipelineFailureContinueDraftIdRef.current = null
                pipelineFailureInsightContinueRef.current = null
                setPipelineFailureInsight(null)
                setError(isNoisePrecargaErrorMessage(msg.trim()) ? null : msg.trim())
                setPhase('pick')
                setPickFlow('multi')
                setPrecargaProcessingUi({ kind: 'idle' })
              }
              setSubmitting(false)
              return
            }
          }
        }
        if (analysisCtx) {
          trackAiDocumentAnalysisFailed(analysisCtx, resolveAiDocumentAnalysisFailedOutcome(e))
        }
        const errCode = (e as { code?: string }).code
        const errInsight = (e as { documentInsight?: DocumentInsight | null }).documentInsight ?? null
        const hasPipelineInsight =
          errCode === 'DOCUMENTATION_PIPELINE_FAILED' && errInsight != null
        const msg =
          typeof named.userMessage === 'string' && named.userMessage.trim()
            ? named.userMessage.trim()
            : typeof named.message === 'string' && named.message.trim()
              ? named.message.trim()
              : 'No se pudo procesar la documentación. Intentá de nuevo.'
        let displayError: string | null = msg
        if (hasPipelineInsight || isNoisePrecargaErrorMessage(displayError)) {
          displayError = null
        }
        if (mountedRef.current) {
          setPipelineFailureInsight(errCode === 'DOCUMENTATION_PIPELINE_FAILED' ? errInsight : null)
          setError(displayError)
          setPhase('pick')
          setPickFlow('multi')
          setPrecargaProcessingUi({ kind: 'idle' })
          if (hasPipelineInsight && activeDraftId) {
            pipelineFailureContinueDraftIdRef.current = activeDraftId
            pipelineFailureInsightContinueRef.current = errInsight
          } else {
            pipelineFailureContinueDraftIdRef.current = null
            pipelineFailureInsightContinueRef.current = null
          }
        }
      } finally {
        if (mountedRef.current) {
          setSubmitting(false)
        }
        abortRef.current = null
        multiRunTotalBytesRef.current = null
        setPrecargaProcessingUi({ kind: 'idle' })
      }
    },
    [edifyUserId, onPrecargaDraftIdChange]
  )

  useEffect(() => {
    if (!initialFile) return
    onInitialFileConsumed?.()
    void runSingleFilePipeline(initialFile)
  }, [initialFile, onInitialFileConsumed, runSingleFilePipeline])

  useEffect(() => {
    if (!initialMultiFiles?.length || initialFile != null || resumeDraftId) return
    if (initialMultiSeedAppliedRef.current) return
    const q = collectUniquePrecargaManualFiles(initialMultiFiles).slice(
      0,
      PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES
    )
    initialMultiSeedAppliedRef.current = true
    onInitialMultiFilesConsumed?.()
    if (q.length >= 2) {
      setPickFlow('multi')
      setMultiQueue(q)
      setPhase('pick')
    } else if (q.length === 1) {
      void runSingleFilePipeline(q[0])
    }
  }, [
    initialMultiFiles,
    initialFile,
    resumeDraftId,
    onInitialMultiFilesConsumed,
    runSingleFilePipeline
  ])

  useEffect(() => {
    if (!resumeDraftId || initialFile) return

    let cancelled = false

    const load = async () => {
      setSubmitting(true)
      setError(null)
      try {
        const rawDraft = await getProjectAutofillDraft(resumeDraftId)
        if (cancelled || !mountedRef.current) return
        const payload = projectAutofillDraftApiToCardPayload(rawDraft, resumeDraftId)
        if (cancelled || !mountedRef.current) return
        onPrecargaDraftIdChange?.(resumeDraftId)
        setCardPayload(payload)
        setPhase('editor')
      } catch {
        if (cancelled || !mountedRef.current) return
        onPrecargaDraftIdChange?.(null)
        setError('No se pudo recuperar el borrador. Volvé a cargar el documento.')
        setPhase('pick')
        setPickFlow('home')
      } finally {
        if (!cancelled && mountedRef.current) {
          setSubmitting(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [resumeDraftId, initialFile, onPrecargaDraftIdChange])

  const triggerPickPdf = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const triggerPickMultiPdf = useCallback(() => {
    multiFileInputRef.current?.click()
  }, [])

  const consumePickedPdf = useCallback(
    (f: File | null | undefined) => {
      if (!f) return
      if (!isPrecargaManualDocumentationFile(f)) {
        setError('Solo se admiten PDF, imágenes o Excel (.xls, .xlsx).')
        return
      }
      void runSingleFilePipeline(f)
    },
    [runSingleFilePipeline]
  )

  const mergeIntoMultiQueue = useCallback((incoming: File[]) => {
    setError(null)
    const pdfs = collectUniquePrecargaManualFiles(incoming)
    if (pdfs.length === 0) {
      setError('Solo se admiten PDF, imágenes (JPG, PNG, WebP, GIF, etc.) o Excel (.xls / .xlsx).')
      return
    }
    setMultiQueue((prev) => {
      const next = collectUniquePrecargaManualFiles([...prev, ...pdfs])
      if (next.length > PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES) {
        setError(`Podés subir hasta ${PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES} archivos por vez.`)
        return next.slice(0, PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES)
      }
      return next
    })
  }, [])

  const onHiddenFileChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const input = ev.target
      const f = input.files?.[0] ?? null
      input.value = ''
      consumePickedPdf(f ?? undefined)
    },
    [consumePickedPdf]
  )

  const onMultiHiddenFileChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const input = ev.target
      const picked = input.files?.length ? Array.from(input.files) : []
      input.value = ''
      if (picked.length === 0) return
      mergeIntoMultiQueue(picked)
    },
    [mergeIntoMultiQueue]
  )

  const removeMultiAt = useCallback((idx: number) => {
    setMultiQueue((prev) => prev.filter((_, i) => i !== idx))
  }, [])

  const handlePickDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handlePickDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handlePickDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      const f = e.dataTransfer.files?.[0]
      consumePickedPdf(f)
    },
    [consumePickedPdf]
  )

  const handleMultiDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      const list = e.dataTransfer.files
      if (!list?.length) return
      mergeIntoMultiQueue(Array.from(list))
    },
    [mergeIntoMultiQueue]
  )

  const continueDespitePipelineInsight = useCallback(async () => {
    const id = pipelineFailureContinueDraftIdRef.current
    const insightSnap = pipelineFailureInsightContinueRef.current
    setPipelineFailureInsight(null)
    setError(null)
    if (!id) {
      pipelineFailureInsightContinueRef.current = null
      setPhase('pick')
      setPickFlow('home')
      return
    }
    setSubmitting(true)
    try {
      const rawDraft = await getProjectAutofillDraft(id)
      if (!mountedRef.current) return
      let payload = projectAutofillDraftApiToCardPayload(rawDraft, id)
      if (insightSnap && !payload.documentInsight) {
        payload = { ...payload, documentInsight: insightSnap }
      }
      pipelineFailureContinueDraftIdRef.current = null
      pipelineFailureInsightContinueRef.current = null
      onPrecargaDraftIdChange?.(id)
      setCardPayload(payload)
      setPhase('editor')
    } catch {
      if (!mountedRef.current) return
      setError('No se pudo recuperar el borrador. Probá de nuevo o volvé a cargar el documento.')
      setPhase('pick')
      setPickFlow('home')
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
    }
  }, [onPrecargaDraftIdChange])

  const resetToPick = useCallback(() => {
    abortRef.current?.abort()
    pipelineFailureContinueDraftIdRef.current = null
    pipelineFailureInsightContinueRef.current = null
    onPrecargaDraftIdChange?.(null)
    setCardPayload(null)
    setPipelineFailureInsight(null)
    setError(null)
    setBatchPartialRows([])
    setMultiQueue([])
    setPickFlow('home')
    setPhase('pick')
    setSubmitting(false)
    setPrecargaProcessingUi({ kind: 'idle' })
  }, [onPrecargaDraftIdChange])

  const handlePickModalClose = useCallback(() => {
    if (submitting) return
    if (pipelineFailureInsight) {
      setPipelineFailureInsight(null)
      setError(null)
      setPickFlow('home')
      return
    }
    if (pickFlow !== 'home') {
      setError(null)
      setPipelineFailureInsight(null)
      setPickFlow('home')
      return
    }
    handleBack()
  }, [submitting, pickFlow, pipelineFailureInsight, handleBack])

  const goOtrasOpcionesCarga = useCallback(() => {
    setError(null)
    setPipelineFailureInsight(null)
    setPickFlow('home')
  }, [])

  const rootClass = variant === 'embedded' ? styles.rootEmbedded : styles.root
  const cardEmbed =
    variant === 'embedded' ? ('createObraDesktop' as const) : ('default' as const)

  const multiTotalBytes = multiQueue.reduce((a, f) => a + (f.size || 0), 0)
  const multiCanAnalyze =
    multiQueue.length >= 2 && multiQueue.length <= PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES

  const precargaModalVariants = {
    hidden: { opacity: 0, scale: 0.96, y: 16 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.22, ease: 'easeOut' as const }
    }
  }

  return (
    <div className={rootClass}>
      {variant === 'standalone' && (
        <div className={styles.topBar}>
          <button type="button" className={styles.backLink} onClick={handleBack}>
            Volver al inicio
          </button>
        </div>
      )}

      {phase === 'editor' && cardPayload ? (
        <div className={styles.cardWrap}>
          {batchPartialRows.length > 0 ? (
            <div className={styles.batchPartialBanner} role="status">
              {batchPartialRows.some((r) => r.tone === 'error') ? (
                <p className={styles.batchPartialTitle}>Archivos con error o sin rubros válidos:</p>
              ) : null}
              {batchPartialRows.some((r) => r.tone === 'skipped') ? (
                <p
                  className={
                    batchPartialRows.some((r) => r.tone === 'error')
                      ? styles.batchPartialSubtitle
                      : styles.batchPartialTitle
                  }
                >
                  Archivos analizados pero omitidos del merge:
                </p>
              ) : null}
              <ul className={styles.batchPartialList}>
                {batchPartialRows.map((row, idx) => (
                  <li
                    key={`${idx}-${row.fileLabel}-${row.tone}`}
                    className={
                      row.tone === 'skipped' ? styles.batchPartialItemSkipped : styles.batchPartialItemError
                    }
                  >
                    <span className={styles.batchPartialFile}>{row.fileLabel}</span>
                    <span className={styles.batchPartialMsg}>{row.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <ProjectAutofillCard
            payload={cardPayload}
            embedLayout={cardEmbed}
            onRequestChangeSourceDocument={resetToPick}
          />
        </div>
      ) : null}

      {phase !== 'editor' ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={PRECARGA_MANUAL_DOCUMENTATION_FILE_ACCEPT}
            className={styles.fileInputHidden}
            disabled={submitting}
            onChange={onHiddenFileChange}
          />
          <input
            ref={multiFileInputRef}
            type="file"
            accept={PRECARGA_MANUAL_DOCUMENTATION_FILE_ACCEPT}
            multiple
            className={styles.fileInputHidden}
            disabled={submitting}
            onChange={onMultiHiddenFileChange}
          />
          {phase === 'processing' ? (
            precargaProcessingUi.kind === 'multi_upload' ? (
              <div className={styles.serverUploadPanel}>
                <ul className={styles.serverUploadList}>
                  {precargaProcessingUi.rows.map((row) => (
                    <li key={row.key} className={styles.serverUploadRow}>
                      <span className={styles.serverUploadName} title={row.name}>
                        {row.name}
                      </span>
                      <span className={styles.serverUploadStatusCell} aria-hidden>
                        {row.status === 'pending' ? (
                          <span className={styles.serverUploadPendingDot} />
                        ) : row.status === 'uploading' ? (
                          <Loader2 size={20} className={styles.serverUploadSpinner} />
                        ) : row.status === 'done' ? (
                          <Check size={20} className={styles.serverUploadOk} strokeWidth={2.5} />
                        ) : (
                          <AlertCircle size={20} className={styles.serverUploadErr} strokeWidth={2} />
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {precargaProcessingUi.rows.some((r) => r.status === 'error') ? (
                  <p className={styles.serverUploadErrorSummary}>
                    {precargaProcessingUi.rows.find((r) => r.status === 'error')?.error}
                  </p>
                ) : null}
                <button
                  type="button"
                  className={styles.cancelUploadBtn}
                  onClick={() => abortRef.current?.abort()}
                >
                  Cancelar subida
                </button>
              </div>
            ) : precargaProcessingUi.kind === 'multi_enqueue' ? (
              <div className={styles.enqueueWrap}>
                <Loader2 size={36} className={styles.enqueueSpinner} aria-hidden />
                <p className={styles.enqueueText}>Preparando el análisis de tus archivos…</p>
              </div>
            ) : (
              <div className={styles.loaderWrap}>
                <DocumentPipelineLoopLoader
                  pipelinePhase={pipelinePhase}
                  primaryFileOriginalName={pipelinePrimaryName}
                  primaryFileSizeBytes={pipelinePrimarySizeBytes}
                />
              </div>
            )
          ) : (
            <ModalOverlay
              isOpen
              onClose={handlePickModalClose}
              preventClose={submitting}
              zIndex={120}
            >
              <motion.div
                className={styles.precargaModal}
                variants={precargaModalVariants}
                initial="hidden"
                animate="visible"
              >
                <div className={styles.precargaModalHeader}>
                  <h2 className={styles.precargaModalTitle}>Documentación con IA</h2>
                  {pickFlow !== 'home' || pipelineFailureInsight ? (
                    <button
                      type="button"
                      className={styles.precargaModalNavBack}
                      onClick={goOtrasOpcionesCarga}
                      disabled={submitting}
                    >
                      ← Otras opciones de carga
                    </button>
                  ) : null}
                </div>
                <div className={styles.precargaModalBody}>
                  {pickFlow === 'home' ? (
                    <p className={styles.pathIntro}>
                      Elegí si querés analizar un solo archivo o varios legajos (PDF, imágenes u Excel) para la misma
                      obra.
                    </p>
                  ) : null}
                  {pipelineFailureInsight ? (
                    <DocumentInsightPanel
                      insight={pipelineFailureInsight}
                      handlers={{
                        onUploadTechnicalDoc: () => {
                          triggerPickPdf()
                        },
                        onContinueAnyway: () => {
                          void continueDespitePipelineInsight()
                        }
                      }}
                    />
                  ) : null}
                  {error && !pipelineFailureInsight ? (
                    <div className={styles.error}>{error}</div>
                  ) : null}
                  {!pipelineFailureInsight && pickFlow === 'home' ? (
                    <div className={styles.pathChoiceGrid}>
                      <button
                        type="button"
                        className={styles.pathChoiceCard}
                        onClick={() => {
                          setError(null)
                          setPickFlow('single')
                          triggerPickPdf()
                        }}
                        disabled={submitting}
                      >
                        <FileText size={36} strokeWidth={1.5} className={styles.pathChoiceIcon} aria-hidden />
                        <span className={styles.pathChoiceHeading}>Un solo archivo</span>
                        <span className={styles.pathChoiceBody}>
                          PDF, imagen (p. ej. JPG, PNG, WebP) o Excel; ideal cuando todo está en un único archivo.
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.pathChoiceCard}
                        onClick={() => {
                          setError(null)
                          setPickFlow('multi')
                        }}
                        disabled={submitting}
                      >
                        <Files size={36} strokeWidth={1.5} className={styles.pathChoiceIcon} aria-hidden />
                        <span className={styles.pathChoiceHeading}>Varios archivos, misma obra</span>
                        <span className={styles.pathChoiceBody}>
                          PDF, imágenes u Excel; analizalos juntos y el asistente unifica rubros repetidos.
                        </span>
                      </button>
                    </div>
                  ) : null}
                  {!pipelineFailureInsight && pickFlow === 'single' ? (
                    <div
                      className={`${styles.pickDropZone} ${isDragOver ? styles.pickDropZoneDragOver : ''}`}
                      onDragOver={handlePickDragOver}
                      onDragLeave={handlePickDragLeave}
                      onDrop={handlePickDrop}
                    >
                      <FileText size={40} strokeWidth={1.5} className={styles.pickDropIcon} aria-hidden />
                      <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={triggerPickPdf}
                        disabled={submitting}
                      >
                        Elegir archivo
                      </button>
                      <p className={styles.pickDropHint}>
                        Incluye PDF, imagen (JPEG, PNG, WebP, etc.) o Excel (.xls, .xlsx). Podés
                        arrastrar acá.
                      </p>
                    </div>
                  ) : null}
                  {!pipelineFailureInsight && pickFlow === 'multi' ? (
                    <div className={styles.multiPanel}>
                      <p className={styles.multiHint}>
                        Agregá de 2 a {PROJECT_AUTOFILL_DOCUMENTATION_BATCH_MAX_FILES} archivos (PDF, imágenes u
                        Excel). Se analizarán en conjunto para una misma obra.
                      </p>
                      {multiQueue.length === 0 ? (
                        <p className={styles.multiEmptyCue}>
                          Todavía no agregaste archivos. Usá Agregar archivos o arrastrá a la zona de abajo.
                        </p>
                      ) : null}
                      {multiQueue.length > 0 ? (
                        <p className={styles.multiListTitle}>Archivos que se van a analizar juntos</p>
                      ) : null}
                      {multiQueue.length > 0 ? (
                        <ul className={styles.multiFileList}>
                          {multiQueue.map((f, idx) => (
                            <li key={fileListKey(f)} className={styles.multiFileRow}>
                              <span className={styles.multiFileIndex}>{idx + 1}</span>
                              <span className={styles.multiFileName} title={f.name}>
                                {f.name}
                              </span>
                              <span className={styles.multiFileMeta}>
                                {formatDocumentationPipelineFileSize(f.size)}
                              </span>
                              <button
                                type="button"
                                className={styles.multiFileRemove}
                                onClick={() => removeMultiAt(idx)}
                                disabled={submitting}
                                aria-label={`Quitar ${f.name}`}
                              >
                                <X size={18} strokeWidth={2} aria-hidden />
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {multiQueue.length > 0 ? (
                        <p className={styles.multiSummary}>
                          {multiQueue.length} archivo{multiQueue.length === 1 ? '' : 's'} —{' '}
                          {formatDocumentationPipelineFileSize(multiTotalBytes)} en total
                        </p>
                      ) : null}
                      <div
                        className={`${styles.pickDropZone} ${isDragOver ? styles.pickDropZoneDragOver : ''}`}
                        onDragOver={handlePickDragOver}
                        onDragLeave={handlePickDragLeave}
                        onDrop={handleMultiDrop}
                      >
                        <Files size={40} strokeWidth={1.5} className={styles.pickDropIcon} aria-hidden />
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={triggerPickMultiPdf}
                          disabled={submitting}
                        >
                          Agregar archivos
                        </button>
                        <p className={styles.pickDropHint}>
                          PDF, imágenes (JPG, PNG, WebP, GIF, etc.) o Excel. Podés elegir varios a la vez o
                          arrastrarlos acá.
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className={styles.precargaModalFooter}>
                  {pipelineFailureInsight ? (
                    <div className={`${styles.precargaModalFooterRow} ${styles.precargaModalFooterRowSingle}`}>
                      <button type="button" className={styles.footerCancelBtn} onClick={goOtrasOpcionesCarga}>
                        Cancelar
                      </button>
                    </div>
                  ) : pickFlow === 'multi' ? (
                    <div className={styles.precargaModalFooterRow}>
                      <button type="button" className={styles.footerCancelBtn} onClick={handleBack}>
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={() => void runMultiFilePipeline(multiQueue)}
                        disabled={submitting || !multiCanAnalyze}
                      >
                        Analizar documentación en conjunto
                      </button>
                    </div>
                  ) : pickFlow === 'single' || pickFlow === 'home' ? (
                    <div className={`${styles.precargaModalFooterRow} ${styles.precargaModalFooterRowSingle}`}>
                      <button type="button" className={styles.footerCancelBtn} onClick={handleBack}>
                        Cancelar
                      </button>
                    </div>
                  ) : null}
                </div>
              </motion.div>
            </ModalOverlay>
          )}
        </>
      ) : null}
    </div>
  )
}

export default ManualAssistedProjectPrecarga
